/**
 * PlanFilter — pure plan filtering / sorting / active-filter-count helpers.
 *
 * Extracted verbatim from script.js (god-object refactor — step 3). No DOM, no app state:
 * filterPlans/sortPlans take the filter state as a parameter (script.js injects the live global
 * `currentFilters` via thin wrappers, read at call time so a clearFilters() whole-binding
 * reassignment is always honored). computeActiveFilterCount was already parameterized.
 *
 * Classifiers come from the step-2 plan-classify module via a direct ES import (NOT
 * window.WoxomQuote.PlanClassify): bundle modules resolve imports at eval time, which runs before
 * script.js sets its window aliases.
 *
 * `filters` param contract = the full, non-partial currentFilters shape:
 *   { selectedProductType:string, carriers:array, networkType:string, hsaEligible:null|bool,
 *     deductible:{min,max}, oopMax:{min,max}, benefitLevel:{min,max}, metalLevels:array, sort:string }
 * filterPlans does unguarded nested access (filters.deductible.min, filters.carriers.length), so
 * the caller must always pass the full shape (the app's init + clearFilters both guarantee it).
 * metalLevels (lowercase ACA metal levels, empty = all) is the one guarded field — it was added
 * after saved-filter shapes existed, so an absent value reads as "no metal-level filter".
 */
import {
  navTypeOf, getProductType, getNetworkType, isHsaEligible, getOopMax, getBenefitLevel, carrierDisplayLabel,
} from './plan-classify.js';

export function filterPlans(plans, filters) {
  return plans.filter(function(plan) {
    // 1. Product Type (single selection)
    if (filters.selectedProductType !== 'all') {
      if (navTypeOf(getProductType(plan)) !== filters.selectedProductType) return false;
    }

    // 2. Carrier (multi-selection)
    if (filters.carriers.length > 0) {
      var planCarrier = carrierDisplayLabel(plan);
      if (filters.carriers.indexOf(planCarrier) === -1) return false;
    }

    // 3. Network Type (single selection)
    if (filters.networkType !== 'all') {
      if (getNetworkType(plan) !== filters.networkType) return false;
    }

    // 4. HSA Eligible
    if (filters.hsaEligible !== null) {
      if (isHsaEligible(plan) !== filters.hsaEligible) return false;
    }

    // 5. Deductible range
    var planDeductible = plan.deductible || 0;
    if (filters.deductible.min !== null && planDeductible < filters.deductible.min) {
      return false;
    }
    if (filters.deductible.max !== null && planDeductible > filters.deductible.max) {
      return false;
    }

    // 6. OOP Max range
    var planOopMax = getOopMax(plan);
    if (filters.oopMax.min !== null && planOopMax < filters.oopMax.min) {
      return false;
    }
    if (filters.oopMax.max !== null && planOopMax > filters.oopMax.max) {
      return false;
    }

    // 7. Benefit Level range
    var planBenefitLevel = getBenefitLevel(plan);
    if (filters.benefitLevel.min !== null && planBenefitLevel < filters.benefitLevel.min) {
      return false;
    }
    if (filters.benefitLevel.max !== null && planBenefitLevel > filters.benefitLevel.max) {
      return false;
    }

    // 8. ACA metal level (multi-selection). Only plans that carry a matching CMS metal level
    //    survive — picking "Silver" narrows to Silver marketplace plans, not every plan.
    if (Array.isArray(filters.metalLevels) && filters.metalLevels.length > 0) {
      var planMetal = String(plan.metalLevel || '').trim().toLowerCase();
      if (filters.metalLevels.indexOf(planMetal) === -1) return false;
    }

    return true;
  });
}

export function sortPlans(plans, sortBy) {
  const sorted = [...plans];

  switch (sortBy) {
    case 'premium-asc':
      sorted.sort((a, b) => a.calculatedPremium - b.calculatedPremium);
      break;
    case 'premium-desc':
      sorted.sort((a, b) => b.calculatedPremium - a.calculatedPremium);
      break;
    case 'deductible-asc':
      sorted.sort((a, b) => a.deductible - b.deductible);
      break;
    case 'deductible-desc':
      sorted.sort((a, b) => b.deductible - a.deductible);
      break;
    case 'carrier':
      sorted.sort((a, b) => {
        const carrierA = carrierDisplayLabel(a).toLowerCase();
        const carrierB = carrierDisplayLabel(b).toLowerCase();
        return carrierA.localeCompare(carrierB);
      });
      break;
    case 'network':
      sorted.sort((a, b) => (a.network || '').localeCompare(b.network || ''));
      break;
    default:
      // Default order - no sorting
      break;
  }

  return sorted;
}

export function computeActiveFilterCount(f) {
  if (!f) return 0;
  var n = 0;
  if (f.selectedProductType && f.selectedProductType !== 'all') n++;
  if (Array.isArray(f.carriers) && f.carriers.length > 0) n++;
  if (f.networkType && f.networkType !== 'all') n++;
  if (f.hsaEligible !== null && f.hsaEligible !== undefined) n++;
  if (f.deductible && (f.deductible.min !== null || f.deductible.max !== null)) n++;
  if (f.oopMax && (f.oopMax.min !== null || f.oopMax.max !== null)) n++;
  if (f.benefitLevel && (f.benefitLevel.min !== null || f.benefitLevel.max !== null)) n++;
  if (Array.isArray(f.metalLevels) && f.metalLevels.length > 0) n++;
  if (f.sort && f.sort !== 'premium-asc') n++;
  return n;
}

export const PlanFilter = { filterPlans, sortPlans, computeActiveFilterCount };
